// viewport-settle.js
// Coalesce a Chrome-Android visualViewport resize *train* into a single settled
// callback. Showing/hiding the URL bar fires a burst of resize events as the
// viewport snaps to the visible area over ~155-215 ms (see #1372); re-solving
// layout per-event thrashes and produces visible multi-rescale. Debounce on a
// quiet window that marks the train's end.
//
// visualViewport resize has a SECOND trigger, unrelated to the address bar: the page's
// viewport meta carries no interactive-widget, so Chrome Android's default
// resizes-visual applies — the on-screen keyboard shrinks visualViewport.height while
// leaving the layout viewport (innerHeight) untouched, and fires the identical resize
// train (interaction-hardening-sibling-parity-audit). A keyboard shrink is never a
// meaningful viewport change for layout purposes, so both the settle callback and the
// stable-height reader below suppress/redirect around it. Accepted trade-off: a genuine
// address-bar transition while a text input is focused won't re-solve until the next
// settle without focus — rare, and the layout it holds is the right one.

export const VV_SETTLE_MS = 64; // play-test: quiet window marking a visualViewport resize train's end (frames land ~16ms apart); re-tune on-device.

// The elements that raise the on-screen keyboard on focus — the app's only two text
// inputs (game-start-identity-overlay.js's name field, game-start-password-field.js's
// password field, which also becomes type=text under its show/hide toggle) plus
// textarea/contenteditable, cheap to cover in a shared primitive so a future input
// isn't silently under-guarded. input[type=button]/[type=submit] are deliberately
// excluded — every button in the app is a <button> element, not an <input>, but the
// type distinction matters for a future one: focusable, and they don't raise a keyboard.
const KEYBOARD_RAISING_SELECTOR =
  'input[type="text"], input[type="password"], textarea, [contenteditable]';

function keyboardRaiserFocused() {
  // activeElement is Element | null — every Element has .matches(), so only the outer
  // nullable-result chain is real.
  return document.activeElement?.matches(KEYBOARD_RAISING_SELECTOR) ?? false;
}

// Register `callback` to run once after the visualViewport resize train settles.
// One listener per call; consumers register once at wire/init time (no unregister
// path — nothing tears these down for the page's life). Checked at settle, not at
// event time — that's when we'd act on it: a keyboard closing before the quiet
// window elapses must not suppress the callback that follows.
export function onVisualViewportSettled(callback) {
  let timer;
  window.visualViewport?.addEventListener("resize", () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (keyboardRaiserFocused()) return; // a keyboard shrink, not a layout-relevant resize
      callback();
    }, VV_SETTLE_MS);
  });
}

// The height layout should solve against. Under resizes-visual, while the keyboard is
// open innerHeight is EXACTLY the pre-keyboard height — the layout viewport doesn't
// move — so the keyboard-safe height needs no state tracking, just innerHeight while a
// keyboard-raiser has focus. Otherwise the #1372 address-bar reasoning above:
// visualViewport.height tracks the visible area through a URL-bar transition,
// innerHeight lags it.
export function stableViewportHeight() {
  return keyboardRaiserFocused()
    ? window.innerHeight
    : (window.visualViewport?.height ?? window.innerHeight);
}

// The small-viewport-height probe — shared knowledge between two consumers
// (fullscreen.js#hasRetractableChrome, game-start-layout.js#placeStartAssembly's vh
// read; start-screen-layout-improvements part C). A hidden fixed probe measures
// `100svh` via getBoundingClientRect (the same mechanics `hasRetractableChrome`
// already used for its own two reads, extracted here so a second probe doesn't grow
// alongside a second consumer — a duplicated knowledge unit, not a duplicated
// mechanism). `100svh` is the BAR-SHOWN height: constant across a Chrome-Android
// address-bar show/hide transition (unlike visualViewport.height, which the bar
// resize itself moves) and immune to the on-screen keyboard, since viewport units
// resolve under the page's default `resizes-visual` (no `interactive-widget` meta) —
// so unlike stableViewportHeight() above, this reader needs no keyboard redirect.
//
// Never cache the result: svh collapses to lvh once the document is already
// fullscreen (the chrome that would otherwise retract is already gone), so a value
// cached from outside fullscreen would latch a stale, too-small reading forever once
// the caller enters it — the same never-cache rule `fullscreen.js#canGainHeight`'s own
// header states for its lvh-vs-svh comparison.
export function smallViewportHeight() {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  // why: the same fallback-then-override pairing skat.css uses everywhere else for a
  // viewport unit (`#skat-app`'s `height: 100vh; height: 100dvh;`) — an unsupported `100svh`
  // assignment is invalid and dropped, leaving 100vh in place, so this consumer degrades to
  // a real (if pressure-reactive) height instead of an unset "auto" collapsing to 0. Review
  // finding: this game-start-layout.js's vh source, unlike fullscreen.js's `lvh > svh`
  // comparison (benign on a 0), floors `--gs-ux` at its pressure minimum on a 0 read.
  probe.style.height = "100vh";
  probe.style.height = "100svh";
  document.body.appendChild(probe);
  const svh = probe.getBoundingClientRect().height;
  probe.remove();
  return svh;
}
